import { useCallback, useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { confirmationOfApplicationSchema } from '@/features/ui/Steps/ConfirmationOfApplication/model/schemas';
import { SignUp } from '@/features/ui/Steps/SignUp/SignUp';

import { useFormDraftPlainTools } from '@/hooks/useFormDraftPlainTools';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { FormDataType, UserService } from '@/api/types';

import api from '@/api';

import { SERVICE_CODE, STEP_NUMBER_TO_SIGN_UP } from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { applicantInfoSchema, missingPersonInfoSchema } from './model/schemas';
import { MissingPersonExtractMergedSchema, ServiceProps } from './model/types';
import { ApplicantInfo, DataPreview, MissingPersonExtractResult, MissingPersonInfo } from './ui/steps';

const STEPS: Array<Step<ServiceProps>> = [
  { title: 'Особисті дані зниклого', step: MissingPersonInfo, validationSchema: missingPersonInfoSchema },
  {
    title: 'Особисті дані заявника',
    step: ApplicantInfo,
    validationSchema: applicantInfoSchema,
  },
  { title: 'Підтвердження даних', step: DataPreview, validationSchema: confirmationOfApplicationSchema },
  {
    title: 'Кваліфікований Електронний Підпис',
    step: SignUp,
  },
  { title: 'Результат', step: MissingPersonExtractResult },
];

export const MissingPersonExtract: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);

  const currentService: UserService = getServiceByCode(SERVICE_CODE) as UserService;

  const { isLoading, onDraftCreate, onDraftContinue, onDraftSave, onDraftDelete, currentRequestId } =
    useFormDraftPlainTools({
      serviceCode: SERVICE_CODE,
      setData: (data) => setDefaultValues(data ?? {}),
      stepNumberToSignUp: STEP_NUMBER_TO_SIGN_UP,
    });

  const [defaultValues, setDefaultValues] = useState<FormDataType>({});
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);

  const handleCreateDraft = useCallback(
    async (data?: { stateForm: FormDataType }, specificStep?: (step: number) => void) => {
      return (
        specificStep &&
        onDraftCreate(
          {
            id: currentService.id,
            name: currentService.name,
            code: SERVICE_CODE,
            totalSteps: STEPS.length,
            currentStep: 1,
            lastStepFilled: 0,
          },
          specificStep,
          data,
        )
      );
    },
    [onDraftCreate, currentService.id, currentService.name, currentService.code, STEPS.length],
  );

  const generatePDF = async (formData: MissingPersonExtractMergedSchema): Promise<void> => {
    const payload = await generatePayload({
      requestId: currentRequestId ?? '',
      formData,
      service: currentService,
      withAttachments: false,
    });

    const pdfArtifact = await api.services.generatePdf(currentService.code, payload);
    serviceFileManager.setAttachment(pdfArtifact);
  };

  const submitForm = async (formData: MissingPersonExtractMergedSchema): Promise<void> =>
    api.services
      .submit(
        currentService.code,
        await generatePayload({
          withAttachments: true,
          formData,
          requestId: currentRequestId ?? '',
          service: currentService,
          serviceFileManager,
        }),
        String(formData.allowProfileUpdate),
      )
      .then(() => {
        serviceFileManager.download();
        serviceFileManager.clear();
      });

  return (
    <ServiceFormTemplate
      className="insurance-service"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      skipAvailabilityValidation={(step) => step === 1}
      onDraftContinue={onDraftContinue}
      onDraftSave={onDraftSave}
      onDraftCreate={handleCreateDraft}
      stepProps={{
        onDraftDelete: () => onDraftDelete(currentRequestId ?? ''),
        onDraftCreate: handleCreateDraft,
        generatePDF,
        submitForm,
        serviceFileManager,
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
      }}
    />
  );
};
